import React, { useEffect, useState } from 'react';

/**
 * SaveAsTemplateModal (DISCHARGE-TEMPLATES Phase 1b Slice 1 · FE-3) — the
 * in-app "Save as template" dialog for a `snippets:true` narrative field.
 * Fully controlled by the caller (`isOpen`), same posture as
 * `WriteOffDuesDialog.jsx` — this component owns none of the trigger
 * affordance or the disabled-with-reason logic that gates it (FE-6).
 *
 * Title: required, ≤ 80 chars (server cap, §2.5 `SNIPPET_TITLE_MAX_LENGTH`),
 * live counter. Uniqueness is enforced CLIENT-SIDE ONLY (§2.5 — the server
 * never rejects a duplicate title) against `existingTitles`, the titles
 * already on this field's picker list; the inline hint keeps Save disabled
 * but never blocks re-typing.
 *
 * Preview: read-only, the EXACT current on-screen text (`text` prop) — this
 * modal never edits the field and saving a template never calls
 * `saveDischargeSummary`. First ~200 chars, "Show all" expands.
 *
 * Save is disabled on an empty title, a duplicate title, and while a save
 * request is in flight (no idempotency key exists on this route — the
 * disabled button is the only guard, §2.5).
 *
 * Props:
 *   isOpen          bool
 *   field            canonical catalogue key (e.g. "ot_note") — the ONLY
 *                     identifier this component sends on save; never a
 *                     label
 *   fieldLabel       optional display label for the "For: <label>" line
 *   text             the field's current ON-SCREEN text (unsaved) — the
 *                     preview AND the save payload's `text`
 *   existingTitles   string[] — titles already on this field's picker list
 *                     (client-side dup check, §2.5)
 *   onSave           async ({ field, title, text }) => void — EXACTLY the
 *                     3 keys of the POST body (§2.5, `extra="forbid"`); the
 *                     caller closes the modal on success (controlled)
 *   onClose          () => void — Cancel
 */

const TITLE_MAX_LENGTH = 80;
const PREVIEW_MAX_CHARS = 200;

// Ratified copy (copy-ratified-slice1.md §A #A9-#A12), business stakeholder
// verdict 2026-09-17.
export const SAVE_TEMPLATE_DUPLICATE_TITLE_COPY = 'A template with this title already exists for this field.';
export const SAVE_TEMPLATE_EMPTY_PREVIEW_COPY = 'Nothing typed yet.';
export const SAVE_TEMPLATE_GENERIC_ERROR_COPY = 'Could not save the template. Please try again.';
export const SAVE_TEMPLATE_SHOW_ALL_COPY = 'Show all';
// §A #A12 — NEW, ruling 5 ("required"). Plain grey caution line under the
// Preview block; templates are reused across other patients.
export const SAVE_TEMPLATE_CAUTION_COPY = "Templates are reused for other patients. Do not include this patient's name or details.";

export default function SaveAsTemplateModal({
  isOpen, field, fieldLabel, text, existingTitles = [], onSave, onClose,
}) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setSaving(false);
      setSaveError(null);
      setPreviewExpanded(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedTitle = title.trim();
  const trimmedText = (text || '').trim();
  const isDuplicate = !!trimmedTitle
    && existingTitles.some((t) => (t || '').trim().toLowerCase() === trimmedTitle.toLowerCase());
  const canSave = trimmedTitle.length > 0 && trimmedTitle.length <= TITLE_MAX_LENGTH && !isDuplicate && !saving;

  const preview = previewExpanded || trimmedText.length <= PREVIEW_MAX_CHARS
    ? trimmedText
    : `${trimmedText.slice(0, PREVIEW_MAX_CHARS)}…`;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ field, title: trimmedTitle, text: trimmedText });
    } catch (err) {
      setSaveError(err?.message || SAVE_TEMPLATE_GENERIC_ERROR_COPY);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Save as template"
        data-testid="save-template-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-5 shadow-2xl max-h-[88vh] flex flex-col"
      >
        <h3 className="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0">Save as template</h3>
        {fieldLabel && (
          <p className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1 mb-3.5">{`For: ${fieldLabel}`}</p>
        )}

        <div className="flex items-center justify-between mb-1">
          <label htmlFor="save-template-title" className="text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
            Title
          </label>
          <span className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="save-template-title-count">
            {`${title.length}/${TITLE_MAX_LENGTH}`}
          </span>
        </div>
        <input
          id="save-template-title"
          type="text"
          value={title}
          maxLength={TITLE_MAX_LENGTH}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. &quot;LSCS — standard&quot;"
          data-testid="save-template-title-input"
          className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[13px] mb-1"
        />
        {isDuplicate && (
          <div
            data-testid="save-template-duplicate-hint"
            className="text-[11.5px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-700 rounded-md px-2.5 py-1.5 mb-3"
          >
            {SAVE_TEMPLATE_DUPLICATE_TITLE_COPY}
          </div>
        )}

        <label className="text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 mb-1 block">Preview</label>
        <div
          data-testid="save-template-preview"
          className="text-[12.5px] text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 mb-1 whitespace-pre-wrap overflow-y-auto max-h-28"
        >
          {trimmedText ? preview : SAVE_TEMPLATE_EMPTY_PREVIEW_COPY}
        </div>
        {!previewExpanded && trimmedText.length > PREVIEW_MAX_CHARS && (
          <button
            type="button"
            data-testid="save-template-show-all"
            onClick={() => setPreviewExpanded(true)}
            className="text-[11px] font-semibold text-teal-700 dark:text-teal-300 mb-3 self-start"
          >
            {SAVE_TEMPLATE_SHOW_ALL_COPY}
          </button>
        )}

        {/* §A #A12, ruling 5 — required caution, plain grey, under Preview. */}
        <p data-testid="save-template-caution" className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
          {SAVE_TEMPLATE_CAUTION_COPY}
        </p>

        {saveError && (
          <div data-testid="save-template-error" className="text-[11.5px] text-red-600 dark:text-red-400 mb-2">
            {saveError}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="save-template-cancel"
            className="px-3.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-[12.5px] font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            data-testid="save-template-save"
            className="px-3.5 py-2 rounded-lg border border-teal-600 bg-teal-600 disabled:opacity-60 text-white text-[12.5px] font-semibold"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
